import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Dispatch } from "@reduxjs/toolkit";
import {
  setBaseNavbarProviders,
  setFallbackNavbarProviders,
  setNavbarSelectedProviders,
  type NavbarProviderOption,
} from "../../redux/slices/ehr/navbarProvider.slice";
import {
  setPersistedNavbarProviders,
  setPersistedNurseCareTeam,
  setPersistedNurseDefaultsLocation,
} from "../../redux/slices/auth.slice";
import { setSelectedProviderUuid } from "../../redux/slices/ehr/scheduling.slice";
import { isNurseUser, startsWithNoProviders } from "../utils/breakGlass";
import { RootState } from "../../redux/store/store";
import { useProviderControllerServiceGetAllProviders } from "../../services/queries";

export const ALL_PROVIDERS_PAGE_SIZE = 500;

// Stable fallback: auth rehydrated from an older persisted state has no
// persistedNavbarProviders, and a fresh [] each render would loop the effect.
const NO_PROVIDERS: NavbarProviderOption[] = [];

export const toProviderOption = (provider: any): NavbarProviderOption => ({
  uuid: provider?.uuid,
  userUuid: provider?.userUuid,
  label:
    `${provider?.firstName || ""} ${provider?.lastName || ""}`.trim() ||
    "Unknown Provider",
  avatar: provider?.avatar,
  specialty: Array.isArray(provider?.specialityList)
    ? provider.specialityList
        ?.map((item: any) => item?.name)
        .filter(Boolean)
        .join(", ")
    : "",
});

/**
 * The providers on a nurse's care team: those the provider list flags with
 * `nurseCareTeamMember` when it is asked with her `careTeamUserUuid`. The
 * nurse herself is left out; when she is a provider she is always in the
 * filter already.
 */
export const getNurseCareTeamProviders = (
  providers: any[] | null | undefined,
  ownUuid?: string | null,
): NavbarProviderOption[] =>
  (Array.isArray(providers) ? providers : [])
    .filter((provider: any) => provider?.nurseCareTeamMember === true)
    .map(toProviderOption)
    .filter((provider) => Boolean(provider.uuid) && provider.uuid !== ownUuid);

const sameUuids = (a: NavbarProviderOption[], b: NavbarProviderOption[]) =>
  a.length === b.length &&
  a.every((provider, i) => provider.uuid === b[i]?.uuid);

export const syncSchedulingProviders = (
  dispatch: Dispatch,
  providers: NavbarProviderOption[],
) => {
  dispatch(
    setSelectedProviderUuid(
      providers.map((provider) => ({ ...provider, id: provider.uuid })),
    ),
  );
};

export const useNavbarProviderDefaults = () => {
  const dispatch = useDispatch();
  const { user, currentLocation } = useSelector(
    (state: RootState) => state.auth,
  );
  const selectedProviders = useSelector(
    (state: RootState) => state.navbarProvider.selectedProviders,
  );
  const persistedProviders = useSelector(
    (state: RootState) => state.auth.persistedNavbarProviders ?? NO_PROVIDERS,
  );
  const isProviderUser = user?.roleType === "PROVIDER";
  const isClinicUser = user?.roleType === "CLINIC_USER";
  const isNoDefaultsUser = startsWithNoProviders(user);
  const isNurse = isNurseUser(user);
  const nurseDefaultsLocation = useSelector(
    (state: RootState) => state.auth.persistedNurseDefaultsLocation ?? null,
  );
  const nurseCareTeam = useSelector(
    (state: RootState) => state.auth.persistedNurseCareTeam ?? NO_PROVIDERS,
  );

  const ownProvider = useMemo<NavbarProviderOption | null>(() => {
    if (!isProviderUser || !user?.uuid) return null;
    return {
      uuid: user.uuid,

      userUuid: user?.userId,
      label: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "Me",
      avatar: user?.avatar,
    };
  }, [isProviderUser, user]);

  useEffect(() => {
    dispatch(setBaseNavbarProviders(ownProvider ? [ownProvider] : []));
  }, [ownProvider, dispatch]);

  // Auth holds the user's picks across reloads/navigation; feed them into
  // the filter every screen reads.
  useEffect(() => {
    dispatch(setNavbarSelectedProviders(persistedProviders));
  }, [persistedProviders, dispatch]);

  // Clinic users other than nurses, advocates and front desk staff fall back
  // to every provider at their location.
  const isFallbackEnabled =
    isClinicUser && !isNurse && !isNoDefaultsUser && Boolean(currentLocation);

  // A nurse's care team at her location, in one request so every member is
  // found whatever the screen size or how far the dropdown was scrolled.
  const isNurseDefaultsEnabled =
    isNurse && Boolean(currentLocation) && Boolean(user?.userId);

  // One request serves both: only a nurse sends `careTeamUserUuid`, which
  // flags her care team; without it the list is every provider at the location.
  const { data, isSuccess } = useProviderControllerServiceGetAllProviders(
    {
      page: 0,
      pageSize: ALL_PROVIDERS_PAGE_SIZE,
      locationUuids: currentLocation ? [currentLocation] : undefined,
      careTeamUserUuid: isNurse ? user?.userId : undefined,
      status: true,
    },
    undefined,
    { enabled: isFallbackEnabled || isNurseDefaultsEnabled },
  );

  const locationProviders = useMemo<NavbarProviderOption[]>(() => {
    const content = (data as any)?.data?.content;
    if (!Array.isArray(content)) return [];
    return content.map(toProviderOption).filter((p) => Boolean(p.uuid));
  }, [data]);

  const appliedLocationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isFallbackEnabled) {
      appliedLocationRef.current = null;
      // Drop any location fallback left in the store, e.g. from a previous
      // clinic-user session, so a nurse, advocate or front desk user starts
      // with none.
      if (isNurse || isNoDefaultsUser) {
        dispatch(setFallbackNavbarProviders([]));
      }
      return;
    }
    if (!isSuccess || appliedLocationRef.current === currentLocation) return;

    appliedLocationRef.current = currentLocation;
    if (locationProviders.length === 0) return;
    dispatch(setFallbackNavbarProviders(locationProviders));
  }, [
    isFallbackEnabled,
    isNurse,
    isNoDefaultsUser,
    isSuccess,
    currentLocation,
    locationProviders,
    dispatch,
  ]);

  // First visit to a location: the care team becomes her picks. Later
  // responses for the same location only add newly assigned providers, so
  // her own additions (kept across reloads) are never dropped. The care team
  // is saved too, as the set she cannot deselect.
  useEffect(() => {
    if (!isNurseDefaultsEnabled || !isSuccess) return;

    const careTeam = getNurseCareTeamProviders(
      (data as any)?.data?.content,
      user?.uuid,
    );
    const isFirstVisit = nurseDefaultsLocation !== currentLocation;

    if (isFirstVisit) {
      dispatch(setPersistedNavbarProviders(careTeam));
      dispatch(setPersistedNurseDefaultsLocation(currentLocation));
    } else {
      const missing = careTeam.filter(
        (provider) => !persistedProviders.some((p) => p.uuid === provider.uuid),
      );
      if (missing.length > 0) {
        dispatch(
          setPersistedNavbarProviders([...persistedProviders, ...missing]),
        );
      }
    }
    if (isFirstVisit || !sameUuids(careTeam, nurseCareTeam)) {
      dispatch(setPersistedNurseCareTeam(careTeam));
    }
  }, [
    isNurseDefaultsEnabled,
    isSuccess,
    data,
    user?.uuid,
    nurseDefaultsLocation,
    currentLocation,
    persistedProviders,
    nurseCareTeam,
    dispatch,
  ]);

  // Picks are persisted, so one can outlive the provider being deactivated.
  // Only a provider the response marks inactive is removed: being missing from
  // a list says nothing, as picks can come from another location.
  const { data: inactiveData, dataUpdatedAt: inactiveDataUpdatedAt } =
    useProviderControllerServiceGetAllProviders(
      {
        page: 0,
        pageSize: ALL_PROVIDERS_PAGE_SIZE,
        status: false,
      },
      undefined,
      { enabled: persistedProviders.length > 0 },
    );

  // Applied once per response, so a provider reactivated and picked again is
  // not removed on the strength of an older response.
  const appliedInactiveDataRef = useRef(0);

  useEffect(() => {
    if (
      !inactiveDataUpdatedAt ||
      appliedInactiveDataRef.current === inactiveDataUpdatedAt
    ) {
      return;
    }
    appliedInactiveDataRef.current = inactiveDataUpdatedAt;

    const content = (inactiveData as any)?.data?.content;
    if (!Array.isArray(content)) return;
    const inactiveUuids = new Set(
      content
        .filter((provider: any) => provider?.active === false)
        .map((provider: any) => provider?.uuid),
    );
    const activePicks = persistedProviders.filter(
      (provider) => !inactiveUuids.has(provider.uuid),
    );
    if (activePicks.length !== persistedProviders.length) {
      dispatch(setPersistedNavbarProviders(activePicks));
    }
  }, [inactiveData, inactiveDataUpdatedAt, persistedProviders, dispatch]);

  useEffect(() => {
    syncSchedulingProviders(dispatch, selectedProviders);
  }, [selectedProviders, dispatch]);

  return { locationProviders, isProviderUser, isClinicUser };
};
